import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// 대시보드/관리자가 공유하는 탭 컨테이너 (2026-08-10) — 예전 DashboardPage 한 곳에 있던
// 탭 로직을, URL 이 둘로 갈라지면서 두 페이지가 똑같이 써야 해 컴포넌트로 뽑았다.
//
// 하는 일:
//  - 활성 탭을 URL(?tab=)에 반영 → 새로고침해도 보던 탭에 그대로 머문다(2026-07-17 피드백).
//  - 한 번 연 탭은 계속 마운트해 두고 CSS(hidden)로만 숨긴다(visitedTabs) → 다른 탭 갔다
//    와도 API 를 다시 안 부르고 로딩 스켈레톤도 다시 안 뜬다. 안 가본 탭은 마운트도 안 해
//    불필요한 호출을 막는다.
//  - 탭 라벨 옆 숫자 배지(badges) — 고객 문의 실시간 알림처럼 상위가 세는 값을 표시한다.
//
// props
//  - tabs               : [{ id, label, Component }] (config/sections.jsx 에서 그룹별로 넘김)
//  - badges             : { [id]: number } — 0 초과일 때만 빨간 배지로 표시
//  - onActiveTabChange  : (id) => void — 최초 마운트 포함, 활성 탭이 바뀔 때마다 호출.
//                          활성 탭에 의존하는 상위 로직(예: cx 미확인 카운트 리셋)이 ref 를
//                          동기화하는 용도.
export default function TabbedSections({ tabs, badges = {}, onActiveTabChange, hideTabs = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);

  // ?tab= 이 이 페이지에 없는 탭이면(다른 그룹 딥링크 등) 첫 탭으로.
  const tabParam = searchParams.get("tab");
  const initialTab = tabIds.has(tabParam) ? tabParam : tabs[0].id;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([initialTab]));

  // 외부에서 URL ?tab= 이 바뀔 때(Sidebar 클릭 등) activeTab 을 동기화한다.
  useEffect(() => {
    const target = tabIds.has(tabParam) ? tabParam : tabs[0].id;
    if (target !== activeTab) {
      setActiveTab(target);
      setVisitedTabs((prev) => (prev.has(target) ? prev : new Set(prev).add(target)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  // 활성 탭 변경(최초 마운트 포함)을 상위에 알린다. onActiveTabChange 는 인라인이라
  // 매 렌더 새로 생기므로 deps 에서 제외 — activeTab 이 실제 바뀔 때만 통지한다.
  useEffect(() => {
    onActiveTabChange?.(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  function handleTabClick(id) {
    setActiveTab(id);
    setVisitedTabs((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", id);
      return next;
    }, { replace: true });
  }

  return (
    <div>
      {!hideTabs && (
      <div className="sticky top-14 z-30 -mx-3 mb-3 max-w-full overflow-x-auto px-3 py-2 sm:-mx-6 sm:px-6">
        <div className="inline-flex whitespace-nowrap rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleTabClick(t.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors sm:px-3.5 ${
                activeTab === t.id
                  ? "bg-brand-600 text-white shadow-sm"
                  : "text-ink-500 hover:bg-ink-100 hover:text-ink-800"
              }`}
            >
              {t.label}
              {badges[t.id] > 0 && (
                <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white align-middle">
                  {badges[t.id] > 9 ? "9+" : badges[t.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      )}
      {tabs.map(({ id, Component }) => (
        visitedTabs.has(id) && (
          <div key={id} className={activeTab === id ? "" : "hidden"}>
            <Component />
          </div>
        )
      ))}
    </div>
  );
}
